/**
 * OverlayRoot — portals the currently active overlay to document.body so it
 * floats over any central-column mode (chat, main split, main expanded).
 *
 * Keyboard support: Escape closes the overlay (unless the registered entry
 * explicitly has `dismissOnBackdrop: false` — in which case we also block
 * Escape to avoid accidental dismissals of important actions).
 *
 * See UI_TAXONOMY.md §4, §6, §9.4.
 */

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLayoutStore } from './LayoutProvider';
import { OVERLAYS } from './overlays';

export function OverlayRoot() {
  const { overlay, closeOverlay } = useLayoutStore();

  useEffect(() => {
    if (!overlay) return;
    const entry = OVERLAYS[overlay.id];
    if (!entry) return;
    const dismissable = entry.dismissOnBackdrop ?? true;
    if (!dismissable) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeOverlay();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [overlay, closeOverlay]);

  if (!overlay) return null;

  const entry = OVERLAYS[overlay.id];
  if (!entry) {
    console.error(`[OverlayRoot] No entry for id: "${overlay.id}"`);
    return null;
  }

  const Component = entry.component;
  const dismissable = entry.dismissOnBackdrop ?? true;

  const viewProps = {
    ...((overlay.props ?? {}) as Record<string, unknown>),
    onClose: closeOverlay,
  };

  return createPortal(
    <div
      className="overlay-backdrop"
      onClick={dismissable ? closeOverlay : undefined}
      role="presentation"
    >
      <div
        className="overlay-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <Component {...viewProps} />
      </div>
    </div>,
    document.body,
  );
}
